import { useEffect } from 'react';
import {
  generatePath,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import SummaryInfo from '../../../components/common/overview-summary/info';
import GeneralCompanyInfo from '../../../components/corporate/overview-general-info/general-company-info';
import CompanyOfficial from '../../../components/corporate/overview/company-official';
import TopUpOptions from '../../common/top-up-options';
import CustomerComments from '../../../components/common/overview-comments/comments';
import OverviewStatements from '../../common/statements';
import { OrganizationType } from '../../common/types';
import { CorporateOverviewGeneral } from '../types';
import useOverview from './hooks/use-overview';
import OverviewTopContent from './parts/overview-top-content';
import OverviewPanels from './parts/overview-panels';

import ROUTES from '../../../routes';

import commonStyles from '../../common/common.module.css';

const CorporateCustomerOverview = (): React.JSX.Element => {
  const { companyOfficial, baseInfo } = useAppSelector(
    (state) => state.corporateCustomers.info.overview,
  );
  const {
    baseInformation,
    baseInformationIsLoading,
    totalBalance,
    balances,
    changeLogs,
    changeLogsPagination,
    customerComments,
    customerCommentsPagination,
  } = useAppSelector((state) => state.corporateCustomers.info.general);
  const {
    cardsEnabled,
    incorporationDates,
    sicCodesDictionaries,
    productsCorporate,
  } = useAppSelector((state) => state.customersDictionaries);
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const {
    activePermissions,
    baseCurrency,
    currenciesList,
    currencies: dictionariesCurrencies,
    countriesList: countries,
  } = useAppSelector((state) => state.root);

  const { customerId } = useParams();

  const {
    setChangeLogPanelIsOpen,
    setReopenPopupVisible,
    control,
    correspondenceAddressSameAsRegisteredAddress,
    businessAddressSameAsRegisteredAddress,
    onSubmit,
    isLoading,
    isSuccess,
    errors,
    globalError,
    commentsIsLoading,
    commentsIsFetching,
    getCustomerCommentsAction,
    onAddCommentToCustomer,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInCustomer,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteCommentInCustomer,
    deleteCommentInProgress,
    deleteCommentIsSuccess,
    cofIsLoading,
    setTopUpPanelIsOpen,
    setSelectedTopUpId,
    topUpPanelIsOpen,
    selectedTopUpId,
    reopenPopupState,
    setReopenPopupState,
    setReopenError,
    reopenInProgress,
    reopenPopupVisible,
    reopenReason,
    onReopenOrganization,
    reopenError,
    setReopenReason,
    changeLogsPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
  } = useOverview({
    customerId,
    activePermissions,
    baseInfo,
  });

  const match = useMatch(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW);

  const navigate = useNavigate();

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    let title = baseInformation?.name
      ? `${baseInformation?.name}: Overview`
      : 'Overview';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.name]);

  return (
    <div className={commonStyles.customerContent}>
      <OverviewTopContent
        setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        setReopenPopupVisible={setReopenPopupVisible}
      />
      <SummaryInfo
        accounts={baseInformation?.accounts?.length as number}
        currencies={totalBalance?.currency as string[]}
        balance={totalBalance?.balance as number}
        cardsEnabled={cardsEnabled}
        balances={balances}
        currenciesList={currenciesList}
        dictionariesCurrencies={dictionariesCurrencies}
        baseCurrency={baseCurrency}
      />
      <GeneralCompanyInfo
        isLoading={baseInformationIsLoading}
        general={baseInfo as CorporateOverviewGeneral}
        control={control}
        correspondenceAddressSameAsRegisteredAddress={
          correspondenceAddressSameAsRegisteredAddress
        }
        businessAddressSameAsRegisteredAddress={
          businessAddressSameAsRegisteredAddress
        }
        onSubmit={onSubmit}
        requestInProgress={isLoading}
        isSuccess={isSuccess}
        errors={errors}
        globalError={globalError}
        status={baseInformation?.status}
        incorporationDates={incorporationDates}
        sicCodesDictionaries={sicCodesDictionaries}
        productsCorporate={productsCorporate}
        countries={countries}
        activePermissions={activePermissions}
      />
      {activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_LIST,
      ) && (
        <CustomerComments
          comments={customerComments}
          pagination={customerCommentsPagination}
          isLoading={commentsIsLoading}
          isFetching={commentsIsFetching}
          getCommentsList={getCustomerCommentsAction}
          createAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_CREATE_ACTION,
          )}
          editAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_EDIT_ACTION,
          )}
          deleteAvailable={activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_DELETE_ACTION,
          )}
          onAddCommentToCustomer={onAddCommentToCustomer}
          addCommentInProgress={addCommentInProgress}
          addCommentIsSuccess={addCommentIsSuccess}
          onEditCommentInCustomer={onEditCommentInCustomer}
          editCommentInProgress={editCommentInProgress}
          editCommentIsSuccess={editCommentIsSuccess}
          users={users}
          onDeleteCommentInCustomer={onDeleteCommentInCustomer}
          deleteCommentInProgress={deleteCommentInProgress}
          deleteCommentIsSuccess={deleteCommentIsSuccess}
          changeLogsAvailable={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
        />
      )}
      <CompanyOfficial
        isLoading={cofIsLoading}
        companyOfficial={companyOfficial}
        customerId={customerId as string}
        status={baseInformation?.status}
        activePermissions={activePermissions}
      />
      <TopUpOptions
        customerType={OrganizationType.CORPORATE}
        organizationId={customerId}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        setSelectedTopUpId={setSelectedTopUpId}
        status={baseInformation?.status}
      />
      {activePermissions?.includes(
        PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
      ) && (
        <OverviewStatements
          matchPath={ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS}
          cardsRoute={ROUTES.CUSTOMERS_CORPORATE_CARDS}
          routes={{
            transationRoute: ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS,
            cardsRoute: ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_CARDS,
          }}
          accounts={
            baseInformation?.id === customerId
              ? baseInformation?.accounts
              : undefined
          }
          cards={baseInformation?.cards as string[]}
          cardsEnabled={cardsEnabled}
          balances={balances}
        />
      )}
      <OverviewPanels
        topUpPanelIsOpen={topUpPanelIsOpen}
        setTopUpPanelIsOpen={setTopUpPanelIsOpen}
        selectedTopUpId={selectedTopUpId}
        setSelectedTopUpId={setSelectedTopUpId}
        reopenPopupState={reopenPopupState}
        setReopenPopupVisible={setReopenPopupVisible}
        setReopenPopupState={setReopenPopupState}
        setReopenError={setReopenError}
        reopenInProgress={reopenInProgress}
        reopenPopupVisible={reopenPopupVisible}
        reopenReason={reopenReason}
        onReopenOrganization={onReopenOrganization}
        reopenError={reopenError}
        setReopenReason={setReopenReason}
        changeLogsPanelIsOpen={changeLogsPanelIsOpen}
        setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        customerId={customerId}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogs={changeLogs}
        changeLogsPagination={changeLogsPagination}
        changeLogsIsFetching={changeLogsIsFetching}
        getChangeLogsAction={getChangeLogsAction}
      />
      <div id="popup-access" />
    </div>
  );
};

export default CorporateCustomerOverview;
